<template>
  <div ref="eventsRef" :class="rootClass" :style="rootStyle" :id="rendering?.dataSource">
    <ContentGrid :desktop-default-grid="[2, 20]" :mobile-default-grid="[1, 14]">
      <JssRichText
        v-if="fields?.title?.value"
        tag="h2"
        class="upcoming-title"
        :field="fields?.title"
      />
      <DynamicForm
        ref="dynamicFormRef"
        :form="fields?.form"
        @change="handleChange"
        @iconClick="handleIconClick"
      />
      <div class="events-list">
        <site-link
          v-for="event in events"
          :key="event.id"
          class="events-list-item"
          :link="event?.fields?.url"
          target="_blank"
        >
          <AdaptiveImage
            class="events-list-item-bg"
            :field="{ fields: event?.fields?.cover?.value }"
            :isBackground="true"
            :lazy-load="false"
            :enableScrollDetection="false"
            :show="true"
            :showImageRedius="false"
          >
            <JssRichText
              class="events-list-item-type"
              :field="event?.fields?.eventType?.value?.text"
            />
          </AdaptiveImage>
          <div class="events-list-item-content">
            <JssRichText class="events-list-item-content-title" :field="event?.fields?.title" />
            <div class="events-list-item-content-date">
              {{
                formatEventDate(
                  event?.fields?.eventsStartDate?.value,
                  event?.fields?.eventsEndDate?.value
                )
              }}
            </div>
            <div class="events-list-item-content-region">
              <div v-for="(region, index) in event?.fields?.region?.value" :key="index">
                {{ region?.fields?.text?.value
                }}{{ index < event?.fields?.region?.value?.length - 1 ? ', ' : '' }}
              </div>
            </div>
            <SiteButton v-bind="fields?.button" />
          </div>
        </site-link>
      </div>
      <div class="events-nodata" v-if="showNodata">
        <JssRichText :field="fields?.noDataText" />
      </div>
      <Pagination
        v-model:currentPage="currentPage"
        :totalPages="totalPages"
        v-if="totalPages > 1"
      />
    </ContentGrid>
  </div>
</template>

<script>
import { computed, ref, watch, onMounted, nextTick } from 'vue';
import utils from '@/utils';
import api from '@/api';

const MONTHS_FULL = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

// Supports "9/30/2026" (M/D/YYYY), "2026-09-30" and ISO strings.
const parseEventDate = (str) => {
  if (!str) return null;
  const s = String(str).trim();
  let match = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (match) {
    return { y: Number(match[3]), m: Number(match[1]), d: Number(match[2]) };
  }
  match = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    return { y: Number(match[1]), m: Number(match[2]), d: Number(match[3]) };
  }
  const date = new Date(s);
  if (isNaN(date.getTime())) return null;
  return { y: date.getUTCFullYear(), m: date.getUTCMonth() + 1, d: date.getUTCDate() };
};

// Formats e.g. "April 14, 2026", "April 14–16, 2026", "April 14–May 2, 2026".
const formatEventDate = (startStr, endStr) => {
  const start = parseEventDate(startStr);
  if (!start) return '';
  const end = parseEventDate(endStr);
  const startText = `${MONTHS_FULL[start.m - 1]} ${start.d}, ${start.y}`;
  if (!end || (end.y === start.y && end.m === start.m && end.d === start.d)) {
    return startText;
  }
  if (end.y === start.y && end.m === start.m) {
    return `${MONTHS_FULL[start.m - 1]} ${start.d}–${end.d}, ${start.y}`;
  }
  const endText = `${MONTHS_FULL[end.m - 1]} ${end.d}, ${end.y}`;
  if (end.y === start.y) {
    return `${MONTHS_FULL[start.m - 1]} ${start.d}–${endText}`;
  }
  return `${startText}–${endText}`;
};

export default {
  name: 'Events',
  props: {
    fields: {
      type: Object,
      default: () => ({}),
    },
    rendering: {
      type: Object,
      default: () => ({}),
    },
    page: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props) {
    const eventsRef = ref(null);
    const dynamicFormRef = ref(null);
    const rootClass = computed(() => utils.overall.baseComClass(props.fields, 'events'));

    const rootStyle = ref(utils.overall.baseComStyle(props.fields));
    const events = ref([]);
    const totalEventsCount = ref(0);
    const showNodata = ref(false);
    const currentPage = ref(1);

    const keywordFilter = ref('');
    const eventTypeFilter = ref('');
    const statusFilter = ref('');
    const regionFilter = ref('');
    const startDateFilter = ref('');
    const endDateFilter = ref('');

    const itemsPerPage = computed(() => {
      const pageSize = props.fields?.pageSize?.value;
      return pageSize ? Number(pageSize) : 6;
    });

    const totalPages = computed(() => {
      return Math.max(1, Math.ceil(totalEventsCount.value / itemsPerPage.value));
    });

    const resetPageAndFetch = () => {
      currentPage.value = 1;
      getEvents();
    };

    const handleChange = (fieldType, fieldName, value) => {
      if (fieldName === 'Region') {
        regionFilter.value = !value || value === 'all-region' ? '' : value;
        resetPageAndFetch();
      } else if (fieldName === 'Event Type') {
        eventTypeFilter.value = !value || value === 'all-events' ? '' : value;
        resetPageAndFetch();
      } else if (fieldName === 'Event Status') {
        statusFilter.value = !value || value === 'all-status' ? '' : value;
        resetPageAndFetch();
      } else if (fieldName === 'Start Date' || fieldName === 'End Date') {
        const formatted = value ? utils.date.formatDateCom(value) : '';
        if (fieldName === 'Start Date') {
          startDateFilter.value = formatted;
          // Disable dates before the start date in the end-date popup
          let startTs = null;
          if (value) {
            const [d, m, y] = String(value).split('-').map(Number);
            startTs = new Date(y, m - 1, d).getTime() - 1;
          }
          dynamicFormRef.value?.updateFields({ 'End Date': { startData: startTs } });
        } else {
          endDateFilter.value = formatted;
        }
        if (!value) {
          // clear 操作：立即 fetch，按剩余条件刷新列表
          resetPageAndFetch();
        } else {
          // 选择操作：保持原逻辑——两个日期都选了且 end >= start 才 fetch
          if (!startDateFilter.value || !endDateFilter.value) return;
          if (endDateFilter.value < startDateFilter.value) return;
          resetPageAndFetch();
        }
      }
    };

    const handleIconClick = (fieldName, value) => {
      if (fieldName !== 'Serach') return;
      keywordFilter.value = value || '';
      resetPageAndFetch();
    };

    const getEvents = async () => {
      const [res, ex] = await api.events.getEvents({
        page: currentPage.value,
        size: itemsPerPage.value,
        eventType: eventTypeFilter.value,
        status: statusFilter.value,
        region: regionFilter.value,
        startDate: startDateFilter.value,
        endDate: endDateFilter.value,
        lang: props.page.itemLanguage,
      });
      if (ex?.list?.length) {
        events.value = ex.list;
        totalEventsCount.value = ex.total || 0;
        showNodata.value = false;
      } else {
        events.value = [];
        totalEventsCount.value = 0;
        showNodata.value = true;
      }
    };

    watch(currentPage, () => {
      eventsRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      getEvents();
    });

    watch(
      () => props.fields,
      (val) => {
        rootStyle.value = utils.overall.baseComStyle(val);
      },
      { deep: true, immediate: true }
    );
    onMounted(() => {
      getEvents();
      nextTick(() => {
        rootStyle.value = { ...utils.overall.baseComStyle(props.fields) };
      });
    });

    return {
      eventsRef,
      dynamicFormRef,
      currentPage,
      totalPages,
      rootClass,
      rootStyle,
      events,
      showNodata,
      getEvents,
      formatEventDate,
      handleChange,
      handleIconClick,
    };
  },
};
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.events {
  position: relative;
  width: 100%;
  padding: 40px 0;

  &-title {
    font-weight: 700;
  }

  .c-dynamic-form {
    margin-top: 30px;

    .f-input__search-icon {
      width: 16px;
      position: relative;
      top: -12px;
      color: #69736f;
      cursor: pointer;
    }
  }

  &-list {
    margin-top: 30px;
    display: flex;
    flex-direction: column;
    gap: 22px;

    &-item {
      border-radius: 10px;
      overflow: hidden;
      background-color: $white;
      width: 100%;

      &-bg {
        width: 100%;
        aspect-ratio: 392 / 190;
        position: relative;
      }

      &-type {
        position: absolute;
        padding: 7px 12px;
        border-radius: 20px;
        background-color: #4b896b;
        top: 16px;
        left: 16px;
        font-size: 12px;
        font-weight: 700;
        text-transform: uppercase;
        color: $white;
      }

      &-content {
        padding: 20px;
        display: flex;
        flex-direction: column;
        gap: 12px;

        &-title {
          font-size: 18px;
          font-weight: 400;
          line-height: 1.3;
          color: #17201d;
        }

        &-date {
          font-size: 14px;
          font-weight: 400;
          line-height: 1.3;
          color: #69736f;
        }

        &-region {
          display: flex;
          font-size: 14px;
          font-weight: 400;
          line-height: 1.3;
          color: #69736f;
        }
      }
    }
  }

  &-nodata {
    padding: 32px;
    color: #69736f;
    text-align: center;
    font-size: 20px;
  }

  @include tablet-landscape {
    padding: 60px 0;

    .c-dynamic-form__items {
      width: 100%;
      flex-direction: row !important;

      .f-dropdown {
        flex: 1;
      }

      .e-date-picker {
        flex: 1;
      }

      .f-input {
        width: 500px;
      }
    }

    &-list {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
    }
  }
}
</style>
